/**
 * Share Link API functions (Magic Links)
 * Matches backend implementation in services/auth.js
 */

import type { MagicLink, MagicLinkValidationResponse } from "@/lib/types/share";

const API_URL =
  import.meta.env.VITE_PUBLIC_IS_PROD === "TRUE"
    ? "https://app.humblytics.com/api/v1/"
    : import.meta.env.VITE_PUBLIC_IS_LOCAL === "TRUE"
    ? "http://localhost:3000/api/v1/"
    : "https://humblytics.onrender.com/api/v1/";

/**
 * Validate magic link and get JWT token
 * No authentication required - uses magicId and password from URL
 */
export async function validateMagicLink(
  magicId: string,
  password: string
): Promise<MagicLinkValidationResponse> {
  const response = await fetch(`${API_URL}validate-magic-property-link`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ magicId, password }),
  });

  if (!response.ok) {
    throw new Error("Invalid or expired share link");
  }

  const data = await response.json();
  return {
    token: data.token,
    property: data.property,
  };
}

/**
 * Create a new magic link (share link)
 */
export async function createMagicLink(
  propertyId: string,
  expiryDateInput: "24-hours" | "2-weeks" | "forever",
  token: string
): Promise<MagicLink> {
  const response = await fetch(`${API_URL}property-magic-link`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ propertyId, expiryDateInput }),
  });

  if (!response.ok) {
    throw new Error("Failed to create share link");
  }

  const data = await response.json();
  return {
    magicLinkId: data.magicLinkId,
    password: data.password,
    link: `${window.location.origin}/s/${data.magicLinkId}/${data.password}`,
    expiryDate: data.expiryDate,
    forever: data.forever,
    propertyId: propertyId,
    domain: "", // Will be populated by backend query
  };
}

/**
 * Get all magic links (share links) for the authenticated user
 */
export async function getMagicLinks(
  token: string
): Promise<MagicLink[]> {
  const response = await fetch(`${API_URL}property-magic-link`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error("Failed to get share links");
  }

  const data = await response.json();
  // Reconstruct link from magicLinkId + password — backend may return single-segment URL
  return (data as any[]).map((item: any) => ({
    ...item,
    link: `${window.location.origin}/s/${item.magicLinkId}/${item.password}`,
  }));
}

/**
 * Revoke/delete a magic link (share link)
 */
export async function revokeMagicLink(
  propertyId: string,
  magicId: string,
  token: string
): Promise<void> {
  const response = await fetch(`${API_URL}property-magic-link`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ propertyId, magicId }),
  });

  if (!response.ok) {
    throw new Error("Failed to revoke share link");
  }
}
